import * as moment from 'moment';
import * as d3 from 'd3';
import * as _ from 'lodash';

import { IAreaConfiguration } from '../../../wtFramework';
import { ComponentDefinition, IGridsterOptions, Group, IGroupConfiguration, ExtendedState, ReferenceState, RefreshClickData, WidgetValues, IValueViewModel, DigitalSetSimpleDictionary, AggregationMode, IReferenceStateForm, TagStringValueDto, Dictionary } from '../../../models';
import { IGroupService, IReferenceStateService, ILoggerService, IExportToCsvService, IDateRangeService, IStateService, IStoreService, IDashboardLoadService, IDigitalSetHelperService, IDataReferenceState, IStringValuesHelperService, IStringValuesService } from '../../../services';
import { IMaximizable, IdataWidget, SeriesConfiguration, IWidgetFactory } from '../../../widgets';
import { IRootScope } from '../../../IRootScope';
import { GridsterOptions } from '../../gridsterOptions';
import { IExportable } from '../../../widgets/models/interfaces';
import { referenceStateRemoved, referenceStateUpdated, wtDroppableDrop, refreshClick } from '../../../common';
import { Store, StoreElement } from '../../../stateManagement';
import {
    IPaneConfiguration,
    lineChartLeftMargin,
    lineChartRightMargin,
    lineChartTopMargin,
    lineChartBottomMargin
} from '../../../framework';
import angular = require('angular');

interface IGroupTrendStateScope extends angular.IScope {
    vm: GroupTrendStateController;
    Model: any;
}

interface ITagsTrendWidget extends IExportable, IMaximizable, IdataWidget { }

interface IGroupTrendState {
    canEdit(): boolean;
    data: IDataReferenceState;
    gridsterOptions: IGridsterOptions;
    loadingMessage: string;
    isDataLoading: boolean;
    isLoading: boolean;
    isTrendEditable: boolean;
    save(): void;
    states: { name: string; guid: string; color: string }[];
    trend: ITagsTrendWidget;
}

class GroupTrendStateController implements IGroupTrendState {
    boundData: WidgetValues = { entries: [] } as WidgetValues;
    data: IDataReferenceState;
    digitalSets: DigitalSetSimpleDictionary;
    formController: IReferenceStateForm;
    gridsterOptions: IGridsterOptions;
    group: Group;
    groupGuid: string;
    isConfigurationVisible: boolean;
    isDataLoading: boolean;
    isLoaded: boolean;
    isLoading: boolean;
    isTrendEditable = false;
    loadingMessage: string;
    seriesFormCtl: angular.IFormController;
    states: { name: string; guid: string; color: string }[];
    stringValues: TagStringValueDto[];
    trend: ITagsTrendWidget;
    viewModels: Dictionary<IValueViewModel>;

    private _configuration: IGroupConfiguration;
    private _currentSelectedConfiguration: IAreaConfiguration;
    private _garbages: (() => void)[] = [];
    private _groupConfigLoaded: angular.IDeferred<void>;
    private _momentReferenceStates: { beginDateTime: moment.Moment; endDateTime: moment.Moment; guid }[];
    private _referenceStates: ExtendedState[];

    static $inject = [
        '$q',
        '$scope',
        '$rootScope',
        '$translate',
        'storeService',
        'groupService',
        'stateService',
        'referenceStateService',
        'widgetFactory',
        'loggerService',
        'exportToCsvService',
        'dateRangeService',
        'dashboardLoadService',
        'digitalSetHelperService',
        'stringValuesService',
        'stringValuesHelperService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _$scope: IGroupTrendStateScope,
        private _$rootScope: IRootScope,
        private _$translate: angular.translate.ITranslateService,
        private _storeService: IStoreService,
        private _groupService: IGroupService,
        private _stateService: IStateService,
        private _referenceStateService: IReferenceStateService,
        private _widgetFactory: IWidgetFactory,
        private _loggerService: ILoggerService,
        private _exportToCsvService: IExportToCsvService,
        private _dateRangeService: IDateRangeService,
        private _dashboardLoadService: IDashboardLoadService,
        private _digitalSetHelperService: IDigitalSetHelperService,
        private _stringValuesService: IStringValuesService,
        private _stringValuesHelperService: IStringValuesHelperService
    ) {
        this._groupConfigLoaded = this._$q.defer();
    }

    $onInit() {
        this.groupGuid = this.group.guid;
        if (!this.data) {
            this.data = {} as IDataReferenceState;
        }
        if (!this.data.groupGuid) {
            this.data.groupGuid = this.groupGuid;
        }
        this.gridsterOptions = new GridsterOptions().options();
        const trend = this._widgetFactory.createWidget('tagsTrend') as ITagsTrendWidget;
        trend.configuration.isEndDateIncluded = true;
        trend.configuration.hideMissing = false;
        trend.configuration.options.panes = this.resetPane(this._$scope);
        this.trend = trend;
        this.isConfigurationVisible = false;
        this.loadingMessage = 'Reading configuration...';
        this.isLoading = true;
        // TODO replace states from store
        const promises = this.group.groupStateCollection.map(gs => this._stateService.getByGuid(gs.stateGuid));
        this._$q.all(promises)
            .then(responses => {
                this.states = responses.map(s => {
                    return {
                        name: s.state.name,
                        guid: s.state.guid,
                        color: s.state.markerColorString
                    };
                });
            });
        this.loadGroupConfiguration()
            .then(() => {
                this._garbages.push(this._$scope.$watch(
                    () => this.trend?.configuration?.series,
                    (newValue, oldValue) => this.onSeriesChange(newValue, oldValue),
                    true
                ));
            });
        this._stateService.getUsedByGroup(this.groupGuid)
            .then(states => this.data.groupStates = states);
        this._garbages.push(this._$scope.$on(refreshClick,
            (_event, data: RefreshClickData) => this.onRefresh(data)
        ));
        this.initWatchers();
    }

    $onDestroy() {
        this._garbages.forEach(g => g());
    }

    private initWatchers(): void {
        this._garbages.push(this._$scope.$on(wtDroppableDrop, (_event, data) => {
            const guid = data.itemId.replace('id', '');
            this._referenceStateService
                .getNewReferenceState(this.groupGuid, guid)
                .then(refState => {
                    this._referenceStates.push(refState);
                    return this._referenceStateService.getReferenceStateAreaConfiguration(refState);
                })
                .then(config => {
                    this.trend.configuration.options.panes[1].areas.push(config);
                    this.selectRefState(config);
                    if (this.formController) {
                        this.formController.$setDirty();
                    }
                });
        }));
        this._garbages.push(this._$scope.$on(referenceStateUpdated, () => {
            this.refreshReferenceStates();
        }));
        this._garbages.push(this._$scope.$on(referenceStateRemoved, () => {
            this.refreshReferenceStates();
        }));
        this._garbages.push(this._$scope.$watch(() => this.data.selectedItem, () => {
            if (!this.data?.selectedItem && this._currentSelectedConfiguration) {
                this._currentSelectedConfiguration.opacity = 1.0;
                this._currentSelectedConfiguration = null;
                this.trend.configuration.options.panes[1].editedArea = null;
                this.trend.configuration.options.panes[0].areas = [];
            }
        }));
        this._garbages.push(this._$scope.$watch(() => this._currentSelectedConfiguration, (newValue, oldValue) => {
            if (oldValue && oldValue !== newValue) {
                oldValue.opacity = 1.0;
            }
        }));
        this._garbages.push(this._$scope.$watch(() => this.data.selectedItem && this.data.selectedItem.beginDateTime,
            (newValue, oldValue) => {
                if (newValue && (!oldValue || (oldValue && moment(newValue).toDate().getTime() !== moment(oldValue).toDate().getTime()))) {
                    const beginDate = moment(newValue);
                    const endDate = moment(this._currentSelectedConfiguration.endDate);
                    const dates = this.checkOverlap(beginDate, endDate, true, false);
                    const date = dates[0].toDate();
                    this._currentSelectedConfiguration.beginDate = date;
                    this.data.selectedItem.beginDateTime = date;
                }
            }
        ));
        this._garbages.push(this._$scope.$watch(() => this.data.selectedItem && this.data.selectedItem.endDateTime,
            (newValue, oldValue) => {
                if (newValue && (!oldValue || (oldValue && moment(newValue).toDate().getTime() !== moment(oldValue).toDate().getTime()))) {
                    const endDate = moment(newValue);
                    const beginDate = moment(this._currentSelectedConfiguration.beginDate);
                    const dates = this.checkOverlap(beginDate, endDate, false, true);
                    const date = dates[1].toDate();
                    this._currentSelectedConfiguration.endDate = date;
                    this.data.selectedItem.endDateTime = date;
                }
            }
        ));
        this._garbages.push(this._$scope.$watch(() => this.data.selectedItem && this.data.selectedItem.stateGuid,
            (newValue, oldValue) => {
                if (newValue !== oldValue) {
                    const refState = _.find(this.data.groupStates, r => (r.state.guid === newValue));
                    if (refState) {
                        this._currentSelectedConfiguration.color = refState.state.markerColorString;
                    }
                }
            }
        ));
        this._garbages.push(this._$scope.$watch(() => this._$rootScope.isEditMode, () => {
            if (this.trend.configuration.options.panes && this.trend.configuration.options.panes.length > 0) {
                this.trend.configuration.options.panes.forEach(p => p.isZoomDisabled = this._$rootScope.isEditMode);
                this.trend.configuration.options.panes[1].editMode = this._$rootScope.isEditMode;
                if (this._$rootScope.isEditMode !== undefined && !this._$rootScope.isEditMode) {
                    this.data.selectedItem = null;
                    this.trend.configuration.options.panes = this.resetPane(this._$scope);
                    this.loadStates();
                }
            }
        }));
        this._garbages.push(
            this._storeService.register(
                (store) => this.storeUpdate(store),
                [StoreElement.DigitalSets, StoreElement.ValueViewModels]
            )
        );
    }

    private storeUpdate(store: Store): void {
        this.digitalSets = store.digitalSetDictionary.dictionary;
        this.viewModels = store.valueViewModels.dictionary;
        this.isLoaded = true;
        this.isLoading = false;
    }

    private onRefresh(data: RefreshClickData) {
        this._groupConfigLoaded.promise.then(() => {
            this.refreshBoundData(data.beginDate, data.endDate, !!data.force);
            this.loadStates(data, !!data.force);
        });
    }

    private refreshBoundData(beginDate: string, endDate: string, force: boolean): angular.IPromise<WidgetValues> {
        if (this.trend && this._dateRangeService.initialized() && beginDate && endDate) {
            this.loadingMessage = 'Loading data';
            this.isLoading = true;
            return this._dashboardLoadService
                .refresh([this.trend], beginDate, endDate, null, null, null, force)
                .then(data => {
                    return this._stringValuesService
                        .getAll()
                        .then(response => {
                            this.stringValues = response;
                            const boundData = _.find(data, d => d.guid === this.trend.guid);
                            let preparedData = this._digitalSetHelperService.mapDigitalSetsValues([boundData], this.digitalSets, this.viewModels, AggregationMode.None);
                            preparedData = this._stringValuesHelperService.mapStringValues(preparedData, this.stringValues, this.viewModels, AggregationMode.None);
                            this.boundData = preparedData && preparedData.length > 0
                                ? preparedData[0]
                                : undefined;
                            return this.boundData;
                        });

                })
                .finally(() => this.isLoading = false);
        }
        else {
            return this._$q.when(undefined);
        }
    }

    private onSeriesChange(newValue, oldValue) {
        const series = this.trend?.configuration?.series;
        if (newValue !== oldValue && series && series.length > 0) {
            this.refreshBoundData(
                this._dateRangeService.beginDate() as string,
                this._dateRangeService.endDate() as string,
                false
            );
        }
    }

    private loadGroupConfiguration() {
        return this._groupService
            .getConfigurationForGroup(this.groupGuid)
            .then(configuration => {
                this._configuration = configuration;
                this.trend.configuration.options.isTitleVisible = false;
                this.trend.configuration.options.panes = this.resetPane(this._$scope);
                this.trend.configuration.series = this
                    .sanitizeConfiguration(this._configuration.seriesConfigurationCollection)
                    .sort((a, b) => d3.ascending(a.sortOrder, b.sortOrder));
                this.isLoading = false;
                this._groupConfigLoaded.resolve();
                return this.loadStates();
            });
    }

    private refreshReferenceStates() {
        this.isDataLoading = true;
        this.loadReferenceStates()
            .finally(() => this.isDataLoading = false);
    }

    private resetPane($scope): IPaneConfiguration[] {
        return [
            {
                height: 81,
                minHeight: 60,
                isXaxisVisible: false,
                isLegendVisible: true,
                areas: [],
                // to ensure range series are vertical aligned
                isTitleVisible: false,
                isTitleLeftAligned: true,
                margins: {
                    marginLeft: lineChartLeftMargin,
                    marginRight: lineChartRightMargin,
                    marginTop: lineChartTopMargin,
                    marginBottom: 10
                }
            },
            {
                height: 8,
                minHeight: 45,
                isXaxisVisible: false,
                isYaxisVisible: false,
                isLegendVisible: false,
                yMin: 0,
                yMax: 1,
                areaClickCallback: this.stateClickCallback(),
                brushCallback: (extent) => {
                    this.trend.configuration.options.panes[1].editedArea.beginDate = extent[0];
                    this.trend.configuration.options.panes[1].editedArea.endDate = extent[1];
                    $scope.$apply();
                },
                brushEndCallback: (extent: [Date, Date]) => {
                    this.onBrushend(extent);
                },
                title: this._$translate.instant('Reference states'),
                isTitleVisible: true,
                isTitleLeftAligned: true,
                editMode: true,
                margins: {
                    marginLeft: lineChartLeftMargin,
                    marginRight: lineChartRightMargin,
                    marginTop: lineChartTopMargin,
                    marginBottom: 10
                }
            },
            {
                height: 11,
                minHeight: 65,
                isXaxisVisible: true,
                isYaxisVisible: false,
                yMin: 0,
                yMax: 1,
                isLegendVisible: false,
                title: this._$translate.instant('Calculated states'),
                isTitleVisible: true,
                isTitleLeftAligned: true,
                editMode: true,
                margins: {
                    marginLeft: lineChartLeftMargin,
                    marginRight: lineChartRightMargin,
                    marginTop: lineChartTopMargin,
                    marginBottom: lineChartBottomMargin
                }
            }
        ];
    }

    private onBrushend(extent: [Date, Date]) {
        const beginDate = moment(extent[0].toISOString());
        const endDate = moment(extent[1].toISOString());
        const checkLeft = !!this.data?.selectedItem && beginDate.toISOString() !== moment(this.data?.selectedItem?.beginDateTime).toISOString();
        const checkRight = !!this.data?.selectedItem && endDate.toISOString() !== moment(this.data?.selectedItem?.endDateTime).toISOString();
        const checked = this.checkOverlap(beginDate, endDate, checkLeft, checkRight);
        const bd = checked[0].toDate();
        const ed = checked[1].toDate();
        this.trend.configuration.options.panes[1].editedArea.beginDate = bd;
        this.trend.configuration.options.panes[1].editedArea.endDate = ed;
        if (this.data?.selectedItem && this.formController) {
            this.data.selectedItem.beginDateTime = bd;
            this.data.selectedItem.endDateTime = ed;
            this.formController.$setDirty();
        }
        this._$scope.$apply();
    }

    private loadReferenceStates(force: boolean = false): angular.IPromise<void> {
        return this._groupService
            .getReferenceStates(this.groupGuid, force)
            .then(responses => {
                this._referenceStates = responses;
                this._momentReferenceStates = this._referenceStates.map(d => {
                    return {
                        beginDateTime: moment(d.beginDateTime),
                        endDateTime: moment(d.endDateTime),
                        guid: d.guid
                    };
                });
                this.trend.configuration.options.panes[1].areas = responses
                    .map(d => this._referenceStateService.getReferenceStateAreaConfiguration(d));
            });
    }

    private checkOverlap(
        beginDate: moment.Moment,
        endDate: moment.Moment,
        checkLeft: boolean,
        checkRight: boolean
    ): [moment.Moment, moment.Moment] {
        return this._referenceStateService.checkOverlap(
            beginDate,
            endDate,
            this.data?.selectedItem?.guid,
            this._momentReferenceStates,
            checkLeft,
            checkRight
        );
    }

    private stateClickCallback() {
        const self = this;
        return (d: any) => {
            self.selectRefState(d);
            this._$scope.$apply();
        };
    }

    private selectRefState(d: any) {
        if (this._$rootScope.isEditMode) {
            if (this.formController?.$dirty) {
                const msg = this._$translate.instant('Save or cancel changes before selecting another reference state.');
                this._loggerService.logWarning(msg);
            } else {
                this.data.selectedItem = _.find(this._referenceStates, r => r.guid === d.guid);
                this._currentSelectedConfiguration = d;
                this.trend.configuration.options.panes[0].areas = [d];
                this.trend.configuration.options.panes[1].editedArea = d;
                d.opacity = 0.5;
            }
        }
    }

    private loadStates(data?: RefreshClickData, force: boolean = false) {
        if (data) {
            this.trend.configuration.options.display.xMin = data.beginDate;
            this.trend.configuration.options.display.xMax = data.endDate;
        }
        this.isDataLoading = true;
        const promises = [
            this.loadReferenceStates(force),
            this._groupService.getActualStates(this.groupGuid, force)
        ];
        return this._$q.all(promises)
            .then(responses => {
                this.trend.configuration.options.panes[2].areas = responses[1].map(rs => {
                    return {
                        name: rs.name,
                        color: rs.color,
                        beginDate: moment(rs.beginDateTime).toDate(),
                        endDate: moment(rs.endDateTime).toDate()
                    };
                });
            }).finally(() => {
                this.isDataLoading = false;
            });
    }

    sanitizeConfiguration(configuration: SeriesConfiguration[]): SeriesConfiguration[] {
        if (configuration) {
            configuration.forEach(sc => {
                if (sc.serieState === undefined || sc.serieState === null) {
                    sc.serieState = 0;
                } else {
                    sc.serieState = +sc.serieState;
                }
            });
        }
        return configuration;
    }

    save() {
        this.isLoading = true;
        this.loadingMessage = 'Saving configuration...';
        this._configuration.seriesConfigurationCollection = this.sanitizeConfiguration(this.trend.configuration.series);
        return this._groupService
            .updateConfigurationForGroup(this._configuration)
            .then(() => {
                this._loggerService.logSuccess(`Configuration of group successfully saved.`, true);
                if (this.seriesFormCtl) {
                    this.seriesFormCtl.$setPristine();
                }
                return this._storeService.dispatchers().valueViewModels.getAll(true);
            })
            .catch(() => {
                this._loggerService.logError('Failed to save configuration.');
            })
            .finally(() => this.isLoading = false);
    }

    cancel() {
        this.loadGroupConfiguration().then(() => {
            this.seriesFormCtl.$setPristine();
        });
    }

    saveReferenceState() {
        this.isDataLoading = true;
        const referenceState: ReferenceState = {
            stateGuid: this.data.selectedItem.stateGuid,
            guid: this.data.selectedItem.guid,
            beginDateTime: this.data.selectedItem.beginDateTime.toISOString(),
            endDateTime: this.data.selectedItem.endDateTime.toISOString(),
            timeZone: 'UTC',
            longComment: this.data.selectedItem.comment
        };

        this._referenceStateService
            .updateReferenceState(this.groupGuid, referenceState)
            .then(() => {
                this._loggerService.logSuccess('Reference state succsessfully saved.', true);
                const areas = this.trend.configuration.options.panes[0].areas;
                if (_.some(areas, (a: any) => a.guid === referenceState.guid)) {
                    _.remove(areas, (a: any) => a.guid === referenceState.guid);
                }
                return this.loadReferenceStates();
            })
            .catch(() => {
                this._loggerService.logError('Failed to save reference state.', true);
            })
            .finally(() => this.isDataLoading = false);
    }

    toggleEditMode() {
        this.isTrendEditable = !this.isTrendEditable;
    }

    canEdit() {
        return true;
    }

    getStateStyle(state) {
        return {
            'width': '20px',
            'height': '20px',
            'background-color': state.color,
            'margin': '2px',
            'cursor': 'grab'
        };
    }

    exportToCsv(): void {
        this.isLoading = true;
        this.loadingMessage = 'Exporting data to CSV';
        this.trend.exportToCsv()
            .finally(() => {
                this.isLoading = false;
            });
    }

    exportStates(): void {
        this.isLoading = true;
        this.loadingMessage = 'Exporting states to CSV';
        this._exportToCsvService
            .getCsvGroupStates(
                this.groupGuid,
                this._dateRangeService.beginDate() as string,
                this._dateRangeService.endDate() as string
            )
            .finally(() => this.isLoading = false);
    }

    toggleConfiguration() {
        if (this.seriesFormCtl && this.seriesFormCtl.$dirty) {
            const msg = this._$translate.instant('Save or cancel changes before closing edit mode.');
            this._loggerService.logWarning(msg);
        } else {
            this.isConfigurationVisible = !this.isConfigurationVisible;
        }
    }

    add() {
        this.setSeriesFormDirty();
    }

    remove() {
        this.setSeriesFormDirty();
    }

    private setSeriesFormDirty(): void {
        if (this.seriesFormCtl) {
            this.seriesFormCtl.$setDirty();
        }
    }
}

export const wtGroupTrendState: ComponentDefinition = {
    componentId: 'wtGroupTrendState',
    controller: GroupTrendStateController,
    template: require('./groupTrendState.component.html'),
    controllerAs: 'vm',
    bindings: {
        data: '=?',
        formController: '<?',
        group: '<'
    }
};